// Created by jarongallo at 3/30/21
import React, { useEffect, useState } from 'react';
import Select, { ActionMeta, MultiValue } from 'react-select';
import UIListInputRow from '../../../../../components/UI/UIListInputRow';
import UICheckbox from '../../../../../components/UI/UICheckbox/UICheckbox';
import UIButton from '../../../../../components/UI/UIButton/UIButton';
import { INVITED_TO_PINEAPPLE, OPTION_PHOTOGRAPHER_CERTIFICATIONS_DROPDOWN_VALUES } from '../../../../../utils/Config/Constants';
import { UI_LABELED_INPUT_TEXT_TYPE } from '../../../../../components/UI/UILabeledInput/config';
import { CHILD_TYPE } from '../../../../../components/UI/UIListInputRow/config';
import Queries from '../../../../../utils/API/Queries';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import { toast } from 'react-toastify';
import { useLazyQuery } from '@apollo/react-hooks';
import { GET_TECHNICIAN_CERTIFICATES } from './queries';
import * as strings from './strings';
import { camelCaseToSentence } from '../../../../../utils/Helpers/StringHelpers';

export default function IndividualTechnicianCertificates({ technicianId }: IndividualTechnicianCertificatesProps) {
	const [technician, setTechnician] = useState<UserType>();
	const [cubiCasaEmail, setCubiCasaEmail] = useState<string>();
	const [isInternalTech, setIsInternalTech] = useState<boolean>();
	const [stripeAccountId, setStripeAccountId] = useState<string | undefined>();

	const client = useApolloClient();

	const [updateTechnicianApi] = useMutation(Queries.UPDATE_USER_MUTATION, {
		onError: (error) => toast.error(strings.errorUpdatingUser(error.message))
	});

	const [getTechnicianCertificates, { data: technicianData }] = useLazyQuery(GET_TECHNICIAN_CERTIFICATES, {
		variables: { id: technicianId }
	});

	useEffect(() => {
		(async () => {
			await getTechnicianCertificates();
		})();
	}, []);

	useEffect(() => {
		(() => {
			if (technicianData) {
				const fetchedTechnician: UserType = technicianData['user'];
				setTechnician(fetchedTechnician);
				setIsInternalTech(fetchedTechnician.isInternalTech);
				setStripeAccountId(fetchedTechnician.stripeAccountId);
				setCubiCasaEmail(fetchedTechnician.cubiCasaEmail);
			}
		})();
	}, [technicianData]);

	if (!technician) return null;

	const updateTechCertificates = async (certificates, onError: (message?: string) => boolean) => {
		const response = await client
			.mutate({
				mutation: Queries.UPDATE_USER_MUTATION,
				variables: { id: technicianId, certificates: certificates }
			})
			.catch(onError);
		const { updateUser } = response['data'];
		if (updateUser) {
			return true;
		} else {
			onError('User update failed');
			return false;
		}
	};

	const addCertificateToTech = async (newCertificate?: string) => {
		if (!newCertificate) return;
		let user = Object.assign({}, technician);
		let newCertificates = Object.assign([], user.certificates);
		newCertificates.push(newCertificate);

		const onError = (message?: string | undefined): boolean => {
			toast.error(strings.errorAdding(message ?? ''));
			return false;
		};

		const success = await updateTechCertificates(newCertificates, onError);

		await getTechnicianCertificates();

		if (success) {
			toast.success(strings.successfulAddition);
		}
	};

	const removeCertificationFromTech = async (certificateToRemove) => {
		let user = Object.assign({}, technician);
		let newCertificates: string[] = Object.assign([], user.certificates).filter((certificate) => certificate !== certificateToRemove);

		const onError = (message?: string | undefined): boolean => {
			toast.error(strings.errorRemoving(message ?? ''));
			return false;
		};

		const success = await updateTechCertificates(newCertificates, onError);
		await getTechnicianCertificates();
		if (success) {
			toast.success(strings.successfulRemoval);
		}
	};
	const updateTechnician = async (key: string, value: any) => {
		const response = await updateTechnicianApi({ variables: { id: technicianId, [key]: value } });
		if (response.data['updateUser']) {
			await getTechnicianCertificates();
			toast.success(strings.updateUserSuccess(camelCaseToSentence(key)));
		} else {
			toast.error(strings.errorUpdatingUser(`User update failed.`));
		}
	};

	const updateTechnicianValue = async (key: string, value: any) => {
		await updateTechnician(key, value);
		await getTechnicianCertificates();
	};

	const getTechnicianCertsValue = () => {
		let array = OPTION_PHOTOGRAPHER_CERTIFICATIONS_DROPDOWN_VALUES;
		array = array.filter((x) => (technician.certificates || []).includes(x.value));
		return array;
	};

	const onInviteChange = () => updateTechnicianValue('stripeAccountId', !stripeAccountId ? INVITED_TO_PINEAPPLE : null);
	const inviteButtonType = !stripeAccountId ? UIButton.FILL_TYPE : UIButton.CANCEL_TYPE;
	const inviteButtonText = !stripeAccountId ? strings.inviteToPineapple : strings.removeFromPineapple;

	const onAddCertificate = async (newValues: MultiValue<{ label: string; value: string }>, actionMeta: ActionMeta<{ label: string; value: string }>) => {
		if (actionMeta.action === 'remove-value') {
			await removeCertificationFromTech(actionMeta.removedValue.value);
		} else {
			await addCertificateToTech(actionMeta?.option?.value);
		}
	};

	return (
		<div className='individualTechnician__content' key={technician.certificates.length}>
			<UIListInputRow label={strings.internalTechHelpText} type={CHILD_TYPE}>
				<UICheckbox
					label={strings.internalTechLabel}
					style={{ marginTop: 12 }}
					onChangeExpansion={() => updateTechnicianValue('isInternalTech', !isInternalTech)}
					status={isInternalTech ? UICheckbox.CHECKED : UICheckbox.UNCHECKED}
				/>
			</UIListInputRow>
			<UIListInputRow
				type={UI_LABELED_INPUT_TEXT_TYPE}
				savedValue={technician.cubiCasaEmail}
				onChangeValue={setCubiCasaEmail}
				onSave={() => updateTechnicianValue('cubiCasaEmail', cubiCasaEmail)}
				value={cubiCasaEmail}
				placeholder={strings.cubiCasaEmailPlaceholder}
			/>
			<UIListInputRow label='certificates' type={CHILD_TYPE}>
				<Select
					placeholder='Add a certificate...'
					classNamePrefix='certificate-select'
					isMulti={true}
					value={getTechnicianCertsValue()}
					onChange={onAddCertificate}
					options={OPTION_PHOTOGRAPHER_CERTIFICATIONS_DROPDOWN_VALUES}
					noOptionsMessage={() => 'This technician has all available certificates.'}
					className='individualTechnician--certificates'
				/>
			</UIListInputRow>
			<UIListInputRow type={CHILD_TYPE} key={`stripeAccountId:${stripeAccountId}`}>
				<UIButton type={inviteButtonType} onClick={onInviteChange} text={inviteButtonText} />
			</UIListInputRow>
		</div>
	);
}
